import { useEffect, useMemo, useState } from "react";
import { BREAKPOINTS } from "./breakpoints";

type MediaQueryType = "max" | "min";

/** Builds a CSS media query string aligned with Tailwind min/max-width semantics. */
export function buildMediaQuery(
  width: number,
  type: MediaQueryType = "max",
): string {
  return type === "max"
    ? `(max-width: ${width - 1}px)`
    : `(min-width: ${width}px)`;
}

/** Synchronous match check — safe for useState initializers. */
export function getMediaQueryMatch(
  width: number,
  type: MediaQueryType = "max",
): boolean {
  if (typeof window === "undefined") {
    return type === "min";
  }
  return window.matchMedia(buildMediaQuery(width, type)).matches;
}

/**
 * Subscribes to a viewport media query via window.matchMedia.
 * Re-renders only when the match boolean changes (native change event).
 */
export function useMediaQuery(
  width: number,
  type: MediaQueryType = "max",
): boolean {
  const query = useMemo(() => buildMediaQuery(width, type), [width, type]);
  const [matches, setMatches] = useState(() => getMediaQueryMatch(width, type));

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    setMatches(mediaQueryList.matches);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };
    mediaQueryList.addEventListener("change", handleChange);
    return () => mediaQueryList.removeEventListener("change", handleChange);
  }, [query]);

  return matches;
}

/** Viewport width below Tailwind `sm` (< 640px). */
export function useIsSmallScreen() {
  return useMediaQuery(BREAKPOINTS.sm, "max");
}

/** Viewport width below Tailwind `md` (< 768px). */
export function useIsMobile() {
  return useMediaQuery(BREAKPOINTS.md, "max");
}

/** Viewport width at Tailwind `md` and above (>= 768px). */
export function useIsTabOrAbove() {
  return useMediaQuery(BREAKPOINTS.md, "min");
}

/** Viewport width at Tailwind `lg` and above (>= 1024px). */
export function useIsLargeScreen() {
  return useMediaQuery(BREAKPOINTS.lg, "min");
}
